import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Check,
  EyeOff,
  Home,
  KeyRound,
  ListChecks,
  Lock,
  Phone,
  X,
} from 'lucide-react';
import {
  ChipInButton,
  Eyebrow,
  SiteShell,
  MadeInIndia,
  richText,
} from '../components/site/SiteChrome';
import { ChatPreview } from '../components/site/ChatPreview';
import { CostsCard } from '../components/site/CostsCard';
import { RunByFamilies } from '../components/site/RunByFamilies';
import { StoreBadges } from '../components/site/StoreBadges';
import { useStoreLinks } from '../lib/siteConfig';
import { useT, type MessageKey } from '../i18n';

// The front door for signed-out visitors (M42, docs/landing-page.md). Every
// privacy claim here must match docs/end-to-end-encryption.md (D107).

const REASONS: Array<{ icon: ReactNode; title: MessageKey; body: MessageKey }> =
  [
    {
      icon: <Lock size={22} />,
      title: 'site.landing.why.private.title',
      body: 'site.landing.why.private.body',
    },
    {
      icon: <EyeOff size={22} />,
      title: 'site.landing.why.noAds.title',
      body: 'site.landing.why.noAds.body',
    },
    {
      icon: <Home size={22} />,
      title: 'site.landing.why.families.title',
      body: 'site.landing.why.families.body',
    },
    {
      icon: <KeyRound size={22} />,
      title: 'site.landing.why.passkeys.title',
      body: 'site.landing.why.passkeys.body',
    },
    {
      icon: <ListChecks size={22} />,
      title: 'site.landing.why.apps.title',
      body: 'site.landing.why.apps.body',
    },
    {
      icon: <Phone size={22} />,
      title: 'site.landing.why.calls.title',
      body: 'site.landing.why.calls.body',
    },
  ];

const CANT_SEE: MessageKey[] = [
  'site.landing.see.cantMessages',
  'site.landing.see.cantMedia',
  'site.landing.see.cantFiles',
  'site.landing.see.cantApps',
  'site.landing.see.cantCalls',
];

const CAN_SEE: MessageKey[] = [
  'site.landing.see.canMembers',
  'site.landing.see.canTimes',
  'site.landing.see.canReactions',
  'site.landing.see.canCalls',
];

const START_CARDS: Array<{ title: MessageKey; body: MessageKey }> = [
  {
    title: 'site.landing.start.invitedTitle',
    body: 'site.landing.start.invitedBody',
  },
  { title: 'site.landing.start.newTitle', body: 'site.landing.start.newBody' },
  {
    title: 'site.landing.start.everywhereTitle',
    body: 'site.landing.start.everywhereBody',
  },
];

function Section({
  id,
  eyebrow,
  title,
  intro,
  children,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      as="section"
      id={id}
      maxW="1080px"
      mx="auto"
      px={4}
      pt={{ base: 14, md: 20 }}
    >
      <VStack align="flex-start" spacing={3} maxW="680px" pb={8}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Heading
          as="h2"
          fontSize={{ base: '30px', md: '40px' }}
          lineHeight={1.1}
        >
          {title}
        </Heading>
        {intro && (
          <Text fontSize="18px" lineHeight="27px" color="ink.body">
            {intro}
          </Text>
        )}
      </VStack>
      {children}
    </Box>
  );
}

function SeeList({
  title,
  items,
  can,
}: {
  title: string;
  items: MessageKey[];
  can: boolean;
}) {
  const t = useT();
  return (
    <Box
      bg={can ? 'surface' : 'accent.tint'}
      borderWidth="1px"
      borderColor={can ? 'line' : 'transparent'}
      borderRadius="card"
      p={{ base: 5, md: 6 }}
    >
      <Heading as="h3" fontSize="20px" pb={4}>
        {title}
      </Heading>
      <VStack align="stretch" spacing={3}>
        {items.map((item) => (
          <HStack key={item} align="flex-start" spacing={3}>
            <Box color={can ? 'muted' : 'accent'} pt="3px">
              {can ? <Check size={18} /> : <X size={18} />}
            </Box>
            <Text fontSize="16px">{t(item)}</Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

export function LandingPage() {
  const t = useT();
  const links = useStoreLinks();
  // Once either store is live, "Works everywhere" points at the apps (D345).
  const hasApps = !!(links.appStore || links.playStore);
  return (
    <SiteShell>
      {/* Hero */}
      <Flex
        maxW="1080px"
        mx="auto"
        px={4}
        pt={{ base: 6, md: 16 }}
        gap={{ base: 10, md: 16 }}
        direction={{ base: 'column', md: 'row' }}
        align="center"
      >
        <VStack align="flex-start" spacing={5} flex="1">
          <Heading
            as="h1"
            fontSize={{ base: '44px', md: '64px' }}
            lineHeight={1}
            letterSpacing="-0.03em"
          >
            {t('site.landing.hero.title')}
          </Heading>
          <Text
            fontSize={{ base: '18px', md: '20px' }}
            lineHeight={1.45}
            color="ink.body"
          >
            {t('site.landing.hero.body')}
          </Text>
          {/* The store badges for this device (D345), once a link is set. */}
          <StoreBadges links={links} height={52} />
          <HStack spacing={3} pt={2} flexWrap="wrap">
            <Button as={RouterLink} to="/login" h="52px" px={7}>
              {t('site.nav.signIn')}
            </Button>
            <ChipInButton />
          </HStack>
          <MadeInIndia long />
          <Text fontSize="15px" color="muted">
            {richText(t('site.landing.hero.inviteOnly'), {
              link: (
                <Link
                  as={RouterLink}
                  to="/request-invite"
                  color="accent"
                  fontWeight={600}
                >
                  {t('site.landing.hero.inviteLink')}
                </Link>
              ),
            })}
          </Text>
        </VStack>
        <Flex flex="1" justify="center" w="full">
          <ChatPreview />
        </Flex>
      </Flex>

      {/* This month's figures, once published (M55, D195). */}
      <RunByFamilies />

      <Section
        eyebrow={t('site.landing.why.eyebrow')}
        title={t('site.landing.why.title')}
        intro={t('site.landing.why.intro')}
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {REASONS.map((reason) => (
            <Box
              key={reason.title}
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              p={5}
            >
              <Flex
                w="44px"
                h="44px"
                borderRadius="14px"
                bg="accent.tint"
                color="accent"
                align="center"
                justify="center"
                mb={4}
              >
                {reason.icon}
              </Flex>
              <Heading as="h3" fontSize="20px" pb={2}>
                {t(reason.title)}
              </Heading>
              <Text color="ink.body" lineHeight="24px">
                {t(reason.body)}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      <Section
        eyebrow={t('site.landing.see.eyebrow')}
        title={t('site.landing.see.title')}
        intro={t('site.landing.see.intro')}
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <SeeList
            title={t('site.landing.see.cantTitle')}
            items={CANT_SEE}
            can={false}
          />
          <SeeList title={t('site.landing.see.canTitle')} items={CAN_SEE} can />
        </SimpleGrid>
        <Text color="muted" fontSize="15px" pt={4} maxW="680px">
          {richText(t('site.landing.see.note'), {
            link: (
              <Link
                as={RouterLink}
                to="/privacy"
                color="accent"
                fontWeight={600}
              >
                {t('site.link.privacyPolicy')}
              </Link>
            ),
          })}
        </Text>
        <Text color="muted" fontSize="15px" pt={2} maxW="680px">
          {richText(t('site.landing.see.strength'), {
            link: (
              <Link
                as={RouterLink}
                to="/help#encryption-strength"
                color="accent"
                fontWeight={600}
              >
                {t('site.link.encryptionStrength')}
              </Link>
            ),
          })}
        </Text>
      </Section>

      <Section
        id="pricing"
        eyebrow={t('site.landing.pricing.eyebrow')}
        title={t('site.landing.pricing.title')}
        intro={t('site.landing.pricing.intro')}
      >
        <Box maxW="680px">
          <CostsCard />
        </Box>
      </Section>

      {/* Why the name (the owner's story). */}
      <Section
        id="name"
        eyebrow={t('site.landing.name.eyebrow')}
        title={t('site.landing.name.title')}
        intro={t('site.landing.name.body')}
      >
        <Link as={RouterLink} to="/about" color="accent" fontWeight={700}>
          {t('site.landing.name.more')}
        </Link>
      </Section>

      <Section
        eyebrow={t('site.landing.start.eyebrow')}
        title={t('site.landing.start.title')}
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {START_CARDS.map((card) => (
            <Box
              key={card.title}
              borderTopWidth="2px"
              borderColor="accent"
              pt={4}
            >
              <Heading as="h3" fontSize="18px" pb={2}>
                {t(card.title)}
              </Heading>
              <Text color="ink.body" lineHeight="24px">
                {t(
                  hasApps && card.body === 'site.landing.start.everywhereBody'
                    ? 'site.landing.start.everywhereBodyApps'
                    : card.body
                )}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
        <Box pt={8}>
          <StoreBadges links={links} legal />
        </Box>
        <HStack spacing={3} pt={8} flexWrap="wrap">
          <Button as={RouterLink} to="/login" h="52px" px={7}>
            {t('site.nav.signIn')}
          </Button>
          <Button
            as={RouterLink}
            to="/request-invite"
            variant="outline"
            colorScheme="gray"
            h="52px"
            px={6}
          >
            {t('site.requestInvite.title')}
          </Button>
        </HStack>
      </Section>
    </SiteShell>
  );
}
